/**
 * Layout Component
 * 
 * Main application layout with navigation and user info.
 * Shows current user from our JWT authentication.
 * Dynamically adjusts navigation based on user role.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getNavigationItems, ROLES } from '../config/permissions';
import NotificationSystem from './NotificationSystem';
import GlobalScannerModal from './GlobalScannerModal';

export default function Layout() {
  const { user, logout, userRole, hasAccess } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showModuleMenu, setShowModuleMenu] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const menuRef = useRef(null);
  const moduleMenuRef = useRef(null);

  // Determine current module based on path
  const currentModule = location.pathname.startsWith('/vehicles') 
    ? 'Vehicle Tracking' 
    : location.pathname.startsWith('/workflows')
      ? 'Workflows'
      : location.pathname.startsWith('/delivery')
        ? 'Delivery'
        : 'Asset Tracking';
  const isVehicleTracking = currentModule === 'Vehicle Tracking';
  const isWorkflows = currentModule === 'Workflows';
  const isDelivery = currentModule === 'Delivery';
  
  // Show dropdown if there are multiple modules available (always show now with 3 modules)
  const hasMultipleModules = true;

  // Get navigation items based on user role and current module
  const navItems = useMemo(() => {
    if (isVehicleTracking) {
      // Vehicle Tracking navigation
      return [
        { path: '/vehicles', label: 'Vehicles', end: true },
        { path: '/vehicles/live', label: 'Live Tracking', end: false },
        { path: '/vehicles/repairs', label: 'Repairs', end: false },
      ];
    }
    if (isWorkflows) {
      // Workflows navigation
      return [
        { path: '/workflows', label: 'Repairs', end: true },
      ];
    }
    if (isDelivery) {
      // Delivery & Fulfillment navigation
      return [
        { path: '/delivery',           label: 'Overview',   end: true },
        { path: '/delivery/shipments', label: 'Shipments',  end: false },
        { path: '/delivery/labels',    label: 'Labels',     end: false },
        { path: '/delivery/tracking',  label: 'Live Map',   end: false },
      ];
    }
    // Asset Tracking navigation (default)
    return getNavigationItems(userRole);
  }, [userRole, isVehicleTracking, isWorkflows, isDelivery]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (moduleMenuRef.current && !moduleMenuRef.current.contains(event.target)) {
        setShowModuleMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.email) return '??';
    const email = user.email;
    const name = email.split('@')[0];
    const parts = name.split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get provider icon
  const getProviderIcon = () => {
    if (user?.authProvider === 'google') {
      return (
        <svg className="w-3 h-3" viewBox="0 0 24 24">
          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        </svg>
      );
    }
    if (user?.authProvider === 'microsoft') {
      return (
        <svg className="w-3 h-3" viewBox="0 0 24 24">
          <path fill="#F25022" d="M1 1h10v10H1z"/>
          <path fill="#00A4EF" d="M1 13h10v10H1z"/>
          <path fill="#7FBA00" d="M13 1h10v10H13z"/>
          <path fill="#FFB900" d="M13 13h10v10H13z"/>
        </svg>
      );
    }
    return null;
  };

  return (
    <div className={`h-screen w-screen flex flex-col overflow-hidden transition-colors duration-200 ${
      isDark 
        ? 'bg-gray-900 text-gray-100' 
        : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Top Navigation Bar */}
      <header className={`h-16 border-b backdrop-blur flex items-center px-6 shrink-0 z-50 transition-colors duration-200 ${
        isDark 
          ? 'border-gray-800 bg-gray-900/95' 
          : 'border-gray-200 bg-white/95'
      }`}>
        <div className="flex items-center gap-3 mr-12">
          {/* Logo - changes based on module */}
          {(() => {
            let logoFile = '/aegislogo.png';
            
            if (location.pathname.startsWith('/vehicles')) {
              logoFile = '/Aegis (Green).png';
            } else if (location.pathname.startsWith('/workflows')) {
              logoFile = '/Aegis (Orange).png';
            } else if (location.pathname.startsWith('/delivery')) {
              logoFile = '/Aegis (Purple).png';
            } else {
              logoFile = '/Aegis (Blue).png';
            }
            
            return (
              <img 
                src={logoFile} 
                alt="AEGIS Logo" 
                className="w-10 h-10 object-contain"
                style={{ filter: 'none' }}
              />
            );
          })()}
          
          {/* Module Selector Dropdown - only show if organization has multiple modules */}
          {hasMultipleModules && (
            <div className="relative" ref={moduleMenuRef}>
              <button
                onClick={() => setShowModuleMenu(!showModuleMenu)}
                style={{ boxSizing: 'content-box' }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs uppercase tracking-wider border-l border-transparent transition-colors outline-none focus:outline-none ${
                  isDark
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-800 hover:border-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 hover:border-gray-300'
                }`}
              >
                <span>{currentModule}</span>
                <svg className={`w-3 h-3 transition-transform ${showModuleMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Module Dropdown Menu */}
              {showModuleMenu && (
              <div className={`absolute left-0 top-full mt-2 w-48 rounded-lg border shadow-xl py-1 z-50 ${
                isDark 
                  ? 'bg-gray-800 border-gray-700' 
                  : 'bg-white border-gray-200'
              }`}>
                <button
                  onClick={() => {
                    navigate('/');
                    setShowModuleMenu(false);
                  }}
                  style={{
                    backgroundColor: !location.pathname.startsWith('/vehicles') && !location.pathname.startsWith('/workflows')
                      ? '#2874ba'
                      : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname.startsWith('/vehicles') || location.pathname.startsWith('/workflows')) {
                      e.target.style.backgroundColor = '#2874ba40';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname.startsWith('/vehicles') || location.pathname.startsWith('/workflows')) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors ${
                    !location.pathname.startsWith('/vehicles') && !location.pathname.startsWith('/workflows')
                      ? 'text-white border-l-2 border-l-white/30'
                      : isDark 
                        ? 'text-gray-300' 
                        : 'text-gray-700'
                  }`}
                >
                  Asset Tracking
                </button>

                {/* Vehicle Tracking Module */}
                <button
                  onClick={() => {
                    navigate('/vehicles');
                    setShowModuleMenu(false);
                  }}
                  style={{
                    backgroundColor: location.pathname.startsWith('/vehicles')
                      ? '#75c043'
                      : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!location.pathname.startsWith('/vehicles')) {
                      e.target.style.backgroundColor = '#75c04340';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!location.pathname.startsWith('/vehicles')) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/vehicles')
                      ? 'text-gray-900 border-l-2 border-l-gray-900/30'
                      : isDark 
                        ? 'text-gray-300' 
                        : 'text-gray-700'
                  }`}
                >
                  Vehicle Tracking
                </button>

                {/* Workflows Module */}
                <button
                  onClick={() => {
                    navigate('/workflows');
                    setShowModuleMenu(false);
                  }}
                  style={{
                    backgroundColor: location.pathname.startsWith('/workflows')
                      ? '#f7913e'
                      : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!location.pathname.startsWith('/workflows')) {
                      e.target.style.backgroundColor = '#f7913e40';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!location.pathname.startsWith('/workflows')) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/workflows')
                      ? 'text-white border-l-2 border-l-white/30'
                      : isDark 
                        ? 'text-gray-300' 
                        : 'text-gray-700'
                  }`}
                >
                  Workflows
                </button>

                {/* Delivery & Fulfillment Module */}
                <button
                  onClick={() => {
                    navigate('/delivery');
                    setShowModuleMenu(false);
                  }}
                  style={{
                    backgroundColor: location.pathname.startsWith('/delivery')
                      ? '#7c3aed'
                      : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!location.pathname.startsWith('/delivery')) {
                      e.target.style.backgroundColor = '#7c3aed40';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!location.pathname.startsWith('/delivery')) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/delivery')
                      ? 'text-white border-l-2 border-l-white/30'
                      : isDark 
                        ? 'text-gray-300' 
                        : 'text-gray-700'
                  }`}
                >
                  Delivery & Fulfillment
                </button>
              </div>
              )}
            </div>
          )}
        </div>

        <nav className="flex items-center gap-1">
          {navItems.map((item) => {
            // Determine if this link should use exact matching (end prop)
            // Use the item's end property if provided, otherwise fall back to defaults
            // For vehicle tracking: only /vehicles should match exactly
            // For asset tracking: only / should match exactly
            const shouldEnd = item.end !== undefined 
              ? item.end 
              : (item.path === '/' || item.path === '/vehicles');
            
            // Get module theme color and text color based on current path
            const getModuleStyles = () => {
            if (location.pathname.startsWith('/vehicles')) {
              return {
                color: '#75c043', // Vehicle Tracking - green
                textColor: 'text-gray-900' // Dark text for light background
              };
              }
              if (location.pathname.startsWith('/workflows')) {
                return {
                  color: '#f7913e', // Workflows - orange
                  textColor: 'text-gray-900' // Dark text for light background
                };
              }
              if (location.pathname.startsWith('/delivery')) {
                return {
                  color: '#7c3aed', // Delivery - violet
                  textColor: 'text-white' // White text for dark background
                };
              }
              return {
                color: '#2874ba', // Asset Tracking - blue (default)
                textColor: 'text-white' // White text for dark background
              };
            };
            
            const moduleStyles = getModuleStyles();
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={shouldEnd}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? moduleStyles.textColor
                      : isDark 
                        ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`
                }
                style={({ isActive }) => ({
                  backgroundColor: isActive ? moduleStyles.color : 'transparent'
                })}
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-4">
          {/* PLX Scanner shortcut */}
          <button
            onClick={() => setShowScanner(true)}
            title="Scan PLX Tag"
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
              isDark
                ? 'text-[#f7c93f] hover:text-[#f7c93f] hover:bg-gray-800'
                : 'text-[#f7c93f] hover:text-[#f7c93f] hover:bg-gray-100'
            }`}
          >
            {/* Scanner icon — custom SVG that looks like a barcode scan frame */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7V5a2 2 0 0 1 2-2h2"/>
              <path d="M17 3h2a2 2 0 0 1 2 2v2"/>
              <path d="M21 17v2a2 2 0 0 1-2 2h-2"/>
              <path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
              <line x1="7" y1="12" x2="7" y2="12.01"/>
              <line x1="10" y1="8" x2="10" y2="16"/>
              <line x1="13" y1="10" x2="13" y2="14"/>
              <line x1="16" y1="8" x2="16" y2="16"/>
            </svg>
          </button>

          {/* Notifications System (Bell + Chat) */}
          <NotificationSystem />
          
          {/* User Profile Dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`flex items-center gap-3 px-3 py-1.5 rounded-full border transition-all ${
                isDark 
                  ? 'bg-gray-800/50 border-gray-700 hover:bg-gray-800 hover:border-gray-600'
                  : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 shadow-sm'
              }`}
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-xs font-bold text-white">
                {getUserInitials()}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm max-w-[150px] truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {user?.email?.split('@')[0] || 'User'}
                </span>
                {getProviderIcon()}
              </div>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className={`absolute right-0 mt-2 w-64 rounded-xl border shadow-2xl py-2 z-50 ${
                isDark 
                  ? 'bg-gray-800 border-gray-700' 
                  : 'bg-white border-gray-200'
              }`}>
                {/* User Info */}
                <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {/* Developer Badge (ONLY for developers - no role/plan shown) */}
                    {user?.isDeveloper ? (
                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-purple-500/30 to-pink-500/30 text-purple-200 border border-purple-500/50">
                        Developer · Full Access
                      </span>
                    ) : (
                      /* Role + Plan Badge (for non-developers only) */
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        userRole === ROLES.ADMIN || userRole === 'company_admin' ? 'bg-purple-500/20 text-purple-300' :
                        'bg-gray-500/20 text-gray-300'
                      }`}>
                        {(user?.roleName || userRole || 'User')}
                        {user?.companyPlanName && ` - ${user.companyPlanName}`}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      via {user?.authProvider || 'email'}
                    </span>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      // Navigate to profile/settings when implemented
                    }}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 ${
                      isDark 
                        ? 'text-gray-300 hover:bg-gray-700/50' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      // Navigate to admin if user has admin_panel or user_management, otherwise settings
                      if (hasAccess('admin_panel') || hasAccess('user_management')) {
                        navigate('/admin');
                      } else {
                        navigate('/settings');
                      }
                    }}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 ${
                      isDark 
                        ? 'text-gray-300 hover:bg-gray-700/50' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings
                  </button>
                  
                  {/* Theme Toggle */}
                  <button
                    onClick={toggleTheme}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between ${
                      isDark 
                        ? 'text-gray-300 hover:bg-gray-700/50' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      {isDark ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                        </svg>
                      )}
                      {isDark ? 'Light Mode' : 'Dark Mode'}
                    </span>
                    {/* Toggle Switch */}
                    <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isDark ? 'bg-gray-600' : 'bg-blue-500'}`}>
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${isDark ? 'translate-x-1' : 'translate-x-5'}`} />
                    </div>
                  </button>
                </div>

                {/* Logout */}
                <div className={`border-t pt-1 mt-1 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-3"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 relative overflow-hidden">
        <Outlet />
      </main>

      {/* Global PLX Scanner Modal */}
      {showScanner && (
        <GlobalScannerModal onClose={() => setShowScanner(false)} />
      )}
    </div>
  );
}
